// Core
import React, { useRef, useEffect } from 'react';
import { Box, Flex, Text, Image, useColorMode } from '@chakra-ui/react';

// Assets
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import crystal1 from 'assets/img/details/crystal-1.png';
import crystal2 from 'assets/img/details/crystal-2.png';
import coin1 from 'assets/img/details/coin-1.png';
import coin2 from 'assets/img/details/coin-2.png';
import screen1 from 'assets/img/how-it-works/screen-1.png';
import screen1White from 'assets/img/how-it-works/screen-1-white.png';
import screen2 from 'assets/img/how-it-works/screen-2.png';
import screen2White from 'assets/img/how-it-works/screen-2-white.png';
import screen3 from 'assets/img/how-it-works/screen-3.png';
import screen3White from 'assets/img/how-it-works/screen-3-white.png';
import screen4 from 'assets/img/how-it-works/screen-4.png';
import screen4White from 'assets/img/how-it-works/screen-4-white.png';
import screen5 from 'assets/img/how-it-works/screen-5.png';
import screen5White from 'assets/img/how-it-works/screen-5-white.png';
import screen6 from 'assets/img/how-it-works/screen-6.png';
import screen6White from 'assets/img/how-it-works/screen-6-white.png';

// Components
import Card from 'components/Card/Card';

const HowItWorksItem = ({ title, description, screenLeft, screenRight }) => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    return (
        <Card 
            className="animation-how-it-works"
            padding="0px" 
            mb="16px" 
            overflow="hidden" 
            minW={{ base: '361px', md: '672px' }} 
            maxW={{ base: '361px', md: '672px' }} 
            minH={{ base: '346px', md: '392px' }} 
            h="100%" 
            scrollSnapAlign="start"
        >
            { isDark && <Box pointerEvents="none" userSelect="none" bg="#099BFF" position="absolute" filter="blur(28px)" h="21px" w="100%" mt="-12px" />}
            <Box pointerEvents="none" userSelect="none" p="16px" zIndex="3">
                <Box 
                  display={{base: 'block', md: 'none'}}
                  bg={ isDark ? '#00071C' : '#dfe9fd'} zIndex="-1" filter="blur(40px)" left="-10%" opacity="0.7" w={{base: '100%', sm: '90%', md: 'none'}} h="290px" position="absolute" />
                <Text pointerEvents="none" userSelect="none" variant="header" fontSize={{base: '36px', sm: '48px'}} fontStyle="normal" whiteSpace="break-spaces">{title}</Text>
                <Text pointerEvents="none" userSelect="none" pt="4px" whiteSpace="normal" maxWidth="60%">{description}</Text>
            </Box>
            <Box pointerEvents="none" userSelect="none" zIndex="2" position="absolute" bottom="0" left="0" w="100%" h="50%">
                <Image className="animation-how-it-works-coin1" src={coin2} alt="Coin" position="absolute" bottom="69px" />
                { isDark && <Box className="animation-how-it-works-gradient" position="absolute" bg="#099BFF" filter="blur(40px)" height="200px" width="400px" bottom="0px" borderRadius="100%" transform="rotate(-45deg)" left="-60px" />}
                <Image className="animation-how-it-works-crystal1" src={crystal1} alt="Crystal" position="absolute" bottom="0" />
                <Image className="animation-how-it-works-crystal2" src={crystal2} alt="Crystal" position="absolute" bottom={{base: '25px', sm: '45px'}} left="40px" />
                <Image className="animation-how-it-works-coin2" src={coin1} alt="Coin" position="absolute" bottom="0" left="86px" />
            </Box>
            <Box pointerEvents="none" userSelect="none" zIndex="2" position="absolute" bottom="0" right="0" w="100%" h="100%">
                { isDark && <Box position="absolute" bg="black" opacity="0.6" filter="blur(40px)" height="200px" width="400px" bottom="0px" borderRadius="100%" transform="rotate(-83deg)" right="253px" />}
                <Image className="animation-how-it-works-phone1" src={screenLeft} alt="Screen" position="absolute" maxW={{base: '220px', sm: '340px'}} right={{base: '100px', sm: '238px'}} top="156px" />
                { isDark && <Box position="absolute" bg="black" opacity="0.4" filter="blur(40px)" height="200px" width="540px" bottom="0px" borderRadius="100%" transform="rotate(-83deg)" right="-44px" />}
                <Image className="animation-how-it-works-phone2" src={screenRight} alt="Screen" position="absolute" maxW={{base: '220px', sm: '390px'}} right={{base: '-40px', sm: '-65px'}} top={{base: '120px', sm: '18px'}}  /> 
            </Box>

            { isDark ? 
                <Lines pointerEvents="none" userSelect="none" className="abstract-lines" style={{ zIndex: 1 }} />
                :
                <LinesWhite pointerEvents="none" userSelect="none" className="abstract-lines" style={{ zIndex: 1, opacity: 0.3 }} />
            }
        </Card>
    );
};

const HowItWorks = () => {
    const scrollRef = useRef(null);
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const handleWheelScroll = (event) => {
	    if (!scrollRef.current) {return;}

	    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
	        return; 
	    }

	    event.preventDefault(); 
	    scrollRef.current.scrollLeft += event.deltaY || event.deltaX * 2; 
	};

    const handleMouseDown = (event) => {
        isDragging = true;
        startX = event.pageX - scrollRef.current.offsetLeft;
        scrollLeft = scrollRef.current.scrollLeft;
    };

    const handleMouseMove = (event) => {
        if (!isDragging) {return;}
        event.preventDefault();
        const x = event.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2; 
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
        isDragging = false;
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) {return;}

        el.addEventListener('mousedown', handleMouseDown);
        el.addEventListener('mousemove', handleMouseMove);
        el.addEventListener('mouseup', handleMouseUp);
        el.addEventListener('mouseleave', handleMouseUp);
        el.addEventListener('wheel', handleWheelScroll, { passive: false });

        return () => {
            el.removeEventListener('mousedown', handleMouseDown);
            el.removeEventListener('mousemove', handleMouseMove);
            el.removeEventListener('mouseup', handleMouseUp);
            el.removeEventListener('mouseleave', handleMouseUp);
            el.removeEventListener('wheel', handleWheelScroll);
        };
    }, []);

    return (
        <Box 
		    ref={scrollRef} 
		    overflowX="auto" 
		    overflowY="hidden" 
		    width="calc(100vw + 696px)" 
			minWidth="calc(100vw + 696px)" 
		    ml={{ base: '-385px', md: '-696px' }} 
		    whiteSpace="nowrap" 
		    scrollSnapType="x mandatory"
		    scrollBehavior="smooth"
		    cursor="grab"
		    sx={{
		        '::-webkit-scrollbar': { display: 'none' }, 
		        '-ms-overflow-style': 'none', 
		        'scrollbar-width': 'none'
		    }}>
            <Flex gap="24px" width="max-content" px="0px">
                <Card overflow="hidden" h="100%" opacity="0" minW={{ base: '361px', md: '672px' }} scrollSnapAlign="start" /> 

                <HowItWorksItem scrollSnapAlign="start" title="All-in-One App" description="Download LMCT+ to access discounts, giveaways, and bonuses for maximum benefits!" screenLeft={ isDark ? screen1 : screen1White} screenRight={ isDark ? screen2 : screen2White} />
                <HowItWorksItem scrollSnapAlign="start" title="Discounts" description="2000+ businesses across Australia offer exclusive deals for LMCT+ members!" screenLeft={isDark ? screen3 : screen3White } screenRight={isDark ? screen4 : screen4White } />
                <HowItWorksItem scrollSnapAlign="start" title="Giveaways" description="Join us to win cars, houses, cash, and other amazing rewards!" screenLeft={isDark ? screen5 : screen5White } screenRight={isDark ? screen6 : screen6White } />   

                <Card overflow="hidden" h="100%" opacity="0" minW={{base: '361px', md: '361px' }} scrollSnapAlign="start" />              
            </Flex>
        </Box>
    );
};

export default HowItWorks;
